import { Component, computed, inject, signal } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ListUsersFacade } from '../../../../../Facades/list-users.facade';
import { User } from '../../../../../Models/avyna/user.model';
import { TranslateService } from '@ngx-translate/core';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvTableLoadingBarComponent } from '../../../../Shared/Components/loading-bar/table-loading-bar.component';
import { AvTableCopyCellComponent } from '../../../../Shared/Components/table-copy-cell/table-copy-cell.component';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { MsgService } from '../../../../Shared/Services/msg.service';
import { UsersService } from '../../../../../Services/users.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';
import { LICENCE_STATE_CLASSES } from '../../../../../Consts/licences.const';
import { LicenceState } from '../../../../../Types/licence-state.type';


@Component({
  selector: 'app-users-table',
  imports: [
    COMMON_IMPORTS,
    AvTableLoadingBarComponent,
    AvTableCopyCellComponent,
  ],
  templateUrl: './users-table.component.html',
  styleUrls: [
    './users-table.component.scss',
    '../../../../../../assets/theme/cutom/_av-table.scss',
    '../../Pages/billing/billing.scss',
  ],
})
export class UsersTableComponent {
  readonly facade = inject(ListUsersFacade);
  private readonly _translationService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly _msgService = inject(MsgService);
  private readonly _usersService = inject(UsersService);

  readonly currentUser = toSignal(this.authService.currentUser$, { initialValue: null });

  permissions = PERMISSIONS;

  readonly rbacOff = computed(() => this.currentUser()?.hasEntitlement('rbac') === false);

  displayedColumns: string[] = [];

  ngOnInit() {
    this.initBaseColumns();
    this.initActionColumn();
  }

  private initBaseColumns(): void {
    this.displayedColumns = this.rbacOff()
      ? ['index', 'name', 'email', 'licence', 'mobilePhone', 'joinDate']
      : ['index', 'name', 'email', 'role', 'licence', 'mobilePhone', 'joinDate'];
  }

  private initActionColumn(): void {
    if (this.currentUser()?.hasPermission(this.permissions.USER_EDIT) || this.currentUser()?.hasPermission(this.permissions.USER_DELETE)) {
      this.displayedColumns.push('action');
    }
  }

  // Licence state

  licenceState(user: User): LicenceState {
    return user.licence_state ?? 'unlicensed';
  }

  licenceStateClass(user: User): string {
    return LICENCE_STATE_CLASSES[this.licenceState(user)];
  }

  licenceStateKey(user: User): string {
    return `app.billing.licences.states.${this.licenceState(user)}`;
  }

  readonly users = computed<User[]>(() => this.facade.users());

  readonly dataSource = computed(() => {
    return new MatTableDataSource<User>(this.users());
  });

  onPageChange(event: PageEvent): void {
    this.facade.setPage(event.pageIndex);
  }

  onSortChange(sort: Sort): void {
    this.facade.setSort(sort.active, sort.direction);
  }

  confirmRemoveSeat(user: User): void {
    this._msgService.showConfirm(
      'app.settings.user_edit.remove_seat.dialog_title',
      'app.settings.user_edit.remove_seat.confirm_text',
      '',
      'person_remove',
      'error'
    ).afterClosed().subscribe(async confirmed => {
      if (!confirmed || !user.id) return;
      await firstValueFrom(this._usersService.removeSeat(user.id));
      this.facade.reload();
    });
  }
}
